import { Link } from "@tanstack/react-router";
import clsx from "clsx";
import type { ReactNode } from "react";
import { ApiError } from "../lib/api";

export function PageHeader({ title, subtitle, actions, eyebrow }: { title: string; subtitle?: ReactNode; actions?: ReactNode; eyebrow?: ReactNode }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <div className="min-w-0">
        {eyebrow && <div className="section-label mb-1.5">{eyebrow}</div>}
        <h1 className="text-[1.625rem] font-bold leading-tight tracking-tight text-[var(--text)]">{title}</h1>
        {subtitle && <p className="muted mt-1.5 max-w-3xl text-[0.9375rem]">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function Card({ title, children, actions, className, bodyClassName, icon, count, flush }: {
  title?: ReactNode;
  children: ReactNode;
  actions?: ReactNode;
  className?: string;
  bodyClassName?: string;
  icon?: ReactNode;
  count?: number;
  /** Body without padding (lists and tables that run edge to edge). */
  flush?: boolean;
}) {
  return (
    <section className={clsx("panel overflow-hidden", className)}>
      {(title || actions) && (
        <header className="flex items-center justify-between gap-3 px-5 pb-3 pt-4">
          <h2 className="flex min-w-0 items-center gap-2 text-[0.9375rem] font-bold">
            {icon && <span className="text-brand-600">{icon}</span>}
            <span className="truncate">{title}</span>
            {count != null && count > 0 && <span className="rounded-full bg-[var(--panel-2)] px-2 py-0.5 text-xs font-bold text-[var(--muted)] ring-1 ring-[var(--line)] num">{count}</span>}
          </h2>
          {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
        </header>
      )}
      <div className={clsx(flush ? "pb-2" : "card-body px-5 pb-5", !(title || actions) && !flush && "pt-5 card-body--untitled", bodyClassName)}>{children}</div>
    </section>
  );
}

export function Empty({ children, icon, action }: { children: ReactNode; icon?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-4 py-10 text-center">
      {icon && <div className="grid h-11 w-11 place-items-center rounded-xl bg-[var(--panel-2)] text-[var(--subtle)] ring-1 ring-[var(--line)]">{icon}</div>}
      <p className="muted max-w-sm text-sm">{children}</p>
      {action}
    </div>
  );
}

/** Small summary number: used on dashboards, workspaces and pulse. */
export function Stat({ label, value, tone, hint, to }: { label: string; value: ReactNode; tone?: "default" | "bad" | "warn" | "good" | "brand"; hint?: ReactNode; to?: string }) {
  const color = { default: "text-[var(--text)]", bad: "text-red-600", warn: "text-amber-600", good: "text-emerald-600", brand: "text-brand-600" }[tone ?? "default"];
  const body = (
    <>
      <div className="section-label">{label}</div>
      <div className={clsx("mt-1.5 text-[1.75rem] font-bold leading-none tracking-tight num", color)}>{value}</div>
      {hint && <div className="muted mt-1.5 text-xs">{hint}</div>}
    </>
  );
  return to ? <Link to={to} className="panel block p-4 transition hover:-translate-y-px hover:border-brand-200 hover:shadow-md">{body}</Link> : <div className="panel p-4">{body}</div>;
}

const AVATAR_TONES = ["bg-brand-100 text-brand-800", "bg-emerald-100 text-emerald-800", "bg-amber-100 text-amber-800", "bg-rose-100 text-rose-800", "bg-sky-100 text-sky-800", "bg-violet-100 text-violet-800"];
export function Avatar({ name, size = 28, className }: { name?: string | null; size?: number; className?: string }) {
  const n = (name ?? "?").trim();
  const initials = n.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join("") || "?";
  const tone = AVATAR_TONES[[...n].reduce((a, c) => a + c.charCodeAt(0), 0) % AVATAR_TONES.length];
  return (
    <span className={clsx("inline-grid shrink-0 place-items-center rounded-full font-bold", tone, className)} style={{ width: size, height: size, fontSize: Math.max(10, size * 0.38) }} aria-hidden>
      {initials}
    </span>
  );
}

export function ErrorNote({ error }: { error: unknown }) {
  if (!error) return null;
  const msg = error instanceof ApiError ? error.message : String((error as Error).message ?? error);
  const reason = error instanceof ApiError && error.details && typeof error.details === "object" && "reason" in error.details ? String((error.details as { reason: string }).reason) : null;
  return (
    <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-3.5 py-2.5 text-sm text-red-800">
      {msg}
      {reason && <span className="block text-xs opacity-80">Why: {reason}</span>}
      {error instanceof ApiError && !!error.details && typeof error.details === "object" && "blockers" in error.details && (
        <ul className="mt-1 list-disc pl-5 text-xs">
          {(error.details as { blockers: Array<{ message: string }> }).blockers.map((b, i) => <li key={i}>{b.message}</li>)}
        </ul>
      )}
      {error instanceof ApiError && !!error.details && typeof error.details === "object" && "fieldErrors" in error.details && (
        <ul className="mt-1 list-disc pl-5 text-xs">
          {Object.entries((error.details as { fieldErrors: Record<string, string[]> }).fieldErrors).map(([k, v]) => (
            <li key={k}>
              {k}: {v.join(", ")}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: string }) {
  return (
    <label className="block text-sm">
      <span className="mb-1.5 block text-[0.8125rem] font-semibold text-[var(--text-2)]">{label}</span>
      {children}
      {hint && <span className="muted mt-1.5 block text-xs">{hint}</span>}
    </label>
  );
}

export function Modal({ title, onClose, children, footer, wide }: { title: string; onClose: () => void; children: ReactNode; footer?: ReactNode; wide?: boolean }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-[#0b0c2a]/45 p-0 backdrop-blur-[2px] sm:items-center sm:p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={clsx("flex max-h-[92vh] w-full flex-col overflow-hidden rounded-t-2xl bg-[var(--panel)] shadow-[var(--shadow-pop)] sm:rounded-2xl", wide ? "max-w-2xl" : "max-w-lg")}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between gap-3 px-5 pb-2 pt-5">
          <h2 className="text-lg font-bold tracking-tight">{title}</h2>
          <button className="btn btn-ghost btn-icon !min-h-0 !h-8 !w-8" onClick={onClose} aria-label="Close">✕</button>
        </header>
        <div className="scroll-thin space-y-4 overflow-auto px-5 pb-5 pt-2">{children}</div>
        {footer && <footer className="flex justify-end gap-2 border-t border-[var(--line)] bg-[var(--panel-2)] px-5 py-3.5">{footer}</footer>}
      </div>
    </div>
  );
}

export const fmtDate = (v: string | null | undefined) => (v ? new Date(v).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : "—");
